import { useForm } from "react-hook-form";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Product, ProductBatch, SaleItem, SaleItemRequest } from "../../../../service/model/store.model";
import { Box, Button, Card, Dialog, DialogTitle, FormControlLabel, FormLabel, Grid, Radio, RadioGroup } from "@mui/material";
import FormField from "../../../../components/form-controls/form-field";
import theme from "../../../../app/app-theme";
import { useEffect, useState } from "react";
import useProducts from "../../../../hooks/use-products";
import { AutocompleteInput } from "../../../../components/form-input-field-wrappers/autocomplete-dropdown-input";
import FormCloseButton from "../../../../components/form-controls/form-close-button";
import StyledChip from "../../../../components/styled-chip";
import { FormTextField } from "../../../../components/form-input-field-wrappers/form-controller-inputs/form-text-field";


interface SaleItemFormProps {
    readonly saleItem?: SaleItem
    readonly editable: boolean
    readonly onClose: () => void
    readonly onSubmit: (data: SaleItemRequest) => void
}

const style = {
    // position: 'absolute' as 'absolute',
    // top: '50%',
    // left: '50%',
    // transform: 'translate(-50%, -50%)',
    // width: 400,
    // bgcolor: 'background.paper',
    // border: '2px solid #000',
    // boxShadow: 24,
    p: 2,
};

const SaleItemForm: React.FC<SaleItemFormProps> = ({ saleItem, editable, onClose, onSubmit }) => {
    const [products] = useProducts();
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(saleItem?.product ?? null);
    const [selectedBatch, setSelectedBatch] = useState<ProductBatch | null>(null);

    const schema = yup.object().shape({
        productId: yup.number()
            .required().nullable(),
        productBatchId: yup.number().required(),
        quantity: yup.number().required().min(1, "Quantity must be at least 1"),
        costPrice: yup.number().required(),
        sellingPrice: yup.number().required().min(0, "Selling price must be at least 0"),
        totalAmount: yup.number().required(),
    });

    const { register, control, watch, setValue, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            productId: saleItem?.product.id,
            productBatchId: saleItem?.productBatch.id,
            quantity: saleItem?.quantity ?? 0,
            costPrice: saleItem?.costPrice ?? 0,
            sellingPrice: saleItem?.sellingPrice ?? 0,
            totalAmount: saleItem?.totalAmount ?? 0,
        }
    });

    useEffect(() => {
        if (selectedBatch) {
            setValue("productBatchId", selectedBatch.id);
            setValue("costPrice", selectedBatch.costPrice);
            setValue("sellingPrice", selectedBatch.sellingPrice);
        }
    }, [selectedBatch]);

    const sellingPrice = watch("sellingPrice");
    const quantity = watch("quantity");
    const productId = watch("productId");
    const batchId = watch("productBatchId");
    
    useEffect(() => {
        const product = (products as Product[]).find(p => p.id === productId)!;
        setSelectedProduct(product);
    }, [productId]);

    useEffect(() => {
        const totalAmount = (sellingPrice || 0) * (quantity || 0);
        setValue("totalAmount", totalAmount);
    }, [sellingPrice, quantity]);

    const onSubmitItemHandler = (data: any) => {
        onSubmit(data as SaleItemRequest)
    };

    const getRadioLabel = (batch: ProductBatch) => {
        return <>
            <Card sx={{ p: 1, display: 'flex', gap: 1, flexWrap: 'wrap', border: '1px solid ' + theme.palette.secondary.dark }}>
                <StyledChip label={`Batch Code: ${batch.batchCode}`} variant="outlined" sx={{width: 'fit-content', backgroundColor: theme.palette.info.light}}/>
                <StyledChip label={`Unit Cost: ${batch.costPrice}`} variant="outlined" sx={{width: 'fit-content', backgroundColor: theme.palette.info.light}} />
                <StyledChip label={`Selling Price: ${batch.sellingPrice}`} variant="outlined" sx={{width: 'fit-content', backgroundColor: theme.palette.info.light}} />
                <StyledChip label={`Stock: ${batch.quantityInStock}`} variant="outlined" sx={{width: 'fit-content', backgroundColor: theme.palette.info.light}} />
                <StyledChip label={`Expiry: ${batch.expiryDate ?? 'No Expiry'}`} variant="outlined" sx={{width: 'fit-content', backgroundColor: theme.palette.info.light}} />
            </Card>
        </>
    }

    return (
        <Dialog open={true} onClose={onClose} sx={style} maxWidth="md" fullWidth>
            <DialogTitle>Sale Item Details</DialogTitle>
            <Box sx={style}>
                <>
                    <FormCloseButton onClose={onClose} />
                    <form>   
                        <FormField>
                            <AutocompleteInput                            
                                    id="productId"
                                    name="productId"
                                    control={control}
                                    label="Select Product"
                                    options={products?.map(p => ({id: p.id, label: p.name})) ?? []}
                            />
                        </FormField>
                        {selectedProduct &&
                        <FormField>
                            <FormLabel id="product-batch-label">Select Batch</FormLabel>
                            <RadioGroup
                                aria-labelledby="product-batch-radio-group"
                                defaultValue={saleItem?.productBatch.id.toString() ?? ''}
                                name="radio-buttons-group"
                            >
                                {selectedProduct && selectedProduct.batches.map(batch => (
                                    <FormControlLabel
                                        key={batch.id}
                                        value={batch.id.toString()}
                                        control={<Radio {...register("productBatchId")} />}
                                        label={getRadioLabel(batch)}
                                        onClick={() => {
                                            setSelectedBatch(batch)
                                        }}
                                    />
                                ))}
                            </RadioGroup>
                        </FormField>
                        }
                        <Grid container direction="row" className="mt-0 my-0" justifyContent='space-between'>
                        <FormField>
                            <FormTextField
                                id="quantity"
                                label="Quantity"
                                name="quantity"
                                type="number"
                                disabled={batchId ? false : true}
                                defaultValue={saleItem?.quantity ?? 0}
                                errorMessage={errors.quantity?.message}
                                register={register}
                            />
                        </FormField>
                        <FormField>
                            <FormTextField
                                id="sellingPrice"
                                label="Selling Price"
                                name="sellingPrice"
                                type="number"
                                disabled={batchId ? false : true}
                                defaultValue={saleItem?.sellingPrice ?? selectedBatch?.sellingPrice ?? 0}
                                errorMessage={errors.sellingPrice?.message}
                                register={register}
                            />
                        </FormField>
                        <FormField>
                            <FormTextField
                                id="costPrice"
                                label="Unit Cost"
                                name="costPrice"
                                type="number"
                                disabled={true}
                                defaultValue={saleItem?.costPrice ?? selectedBatch?.costPrice ?? 0}
                                errorMessage={errors.costPrice?.message}
                                register={register}
                            />
                        </FormField>
                        <FormField>
                            <FormTextField
                                id="totalAmount"
                                label="Total Amount"
                                name="totalAmount"
                                type="number"
                                disabled={true}
                                defaultValue={saleItem?.totalAmount ?? 0}
                                register={register}
                            />
                        </FormField>
                        </Grid>
                        <Grid container direction="row" justifyContent='end' sx={{ mt: 1 }}>
                            <Button type="button" variant="contained" sx={{ mr: 1, bgcolor: `${theme.palette.secondary.main}` }} onClick={onClose}>Cancel</Button>
                            <Button type="button" variant="contained" onClick={handleSubmit(onSubmitItemHandler)}>Submit</Button>
                        </Grid>
                    </form>
                </>
            </Box>
        </Dialog>
    );
}

export default SaleItemForm;